import { useState } from 'react'
import styled from 'styled-components'

import { useOrderSummaryContext } from 'checkout/containers/OrderSummaryContextProvider'
import { ApplicableCostsModal } from 'checkout/panels/OrderSummary/ApplicableCostsModal'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { RotatingCaret } from 'shared/components'
import Divider from 'shared/components/Divider'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { PriceBreakdownLineItemType } from 'shared/enums/PriceBreakdownLineItemType'
import { colors, fonts } from 'shared/lib'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'

export const OrderSummaryPriceBreakDown = () => {
  const { order } = useOrderSummaryContext()
  const isUpgradeFtyEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)
  const isOffersEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_OFFERS)
  const [isApplicableCostsModalOpen, setIsApplicableCostsModalOpen] = useState(false)
  const [isPriceBreakdownOpen, setIsPriceBreakdownOpen] = useState(false)

  const handleClickNote = () => {
    setIsApplicableCostsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsApplicableCostsModalOpen(false)
  }

  const orderSubtotal = order?.pricingBreakdown?.lineItems?.find(
    (lineItem) => lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL,
  )
  const orderPriorityProcessing = orderSubtotal?.subItems?.find(
    (subItem) => subItem?.type === PriceBreakdownLineItemType.PRIORITY_PROCESSING,
  )
  const displayPriceBreakdown =
    isUpgradeFtyEnabled &&
    order?.isInstantShip &&
    orderSubtotal?.subItems?.length > 0 &&
    !!orderPriorityProcessing?.localizedAmount?.amount

  const isWantOrder = isOffersEnabled && order?.wantId

  return (
    <>
      <Container>
        {order?.pricingBreakdown?.lineItems?.map((lineItem) => {
          if (
            lineItem.type === PriceBreakdownLineItemType.APPLICABLE_COSTS &&
            lineItem?.localizedAmount?.amount === 0
          ) {
            return null
          }

          return (
            <div key={lineItem?.type}>
              <PriceLine>
                {lineItem?.type === PriceBreakdownLineItemType.APPLICABLE_COSTS &&
                order?.applicableCostsNote ? (
                  <DetailTitleButton
                    data-qa={getDataQaLabel(lineItem?.type)}
                    onClick={handleClickNote}
                  >
                    {lineItem?.label}
                  </DetailTitleButton>
                ) : (
                  <DetailTitle data-qa={getDataQaLabel(lineItem?.type)}>
                    {lineItem?.label}
                  </DetailTitle>
                )}
                {displayPriceBreakdown ? (
                  <button onClick={() => setIsPriceBreakdownOpen(!isPriceBreakdownOpen)}>
                    <DetailValue
                      data-qa={getDataQaAmount(lineItem?.type)}
                      $hasNumber={/\d/.test(lineItem?.formattedAmount)}
                      $type={lineItem?.type}
                    >
                      {lineItem?.formattedAmount}
                      {lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL && (
                        <span data-qa={CheckoutQA.OrderSummaryBreakdownToggle}>
                          <Caret color={colors.FC2_GREY} isOpen={isPriceBreakdownOpen} />
                        </span>
                      )}
                    </DetailValue>
                  </button>
                ) : (
                  <DetailValue
                    data-qa={getDataQaAmount(lineItem?.type)}
                    $hasNumber={/\d/.test(lineItem?.formattedAmount)}
                    $type={lineItem?.type}
                  >
                    {lineItem?.formattedAmount}
                  </DetailValue>
                )}
              </PriceLine>
              {displayPriceBreakdown &&
                isPriceBreakdownOpen &&
                lineItem?.type === PriceBreakdownLineItemType.SUBTOTAL && (
                  <div>
                    {orderSubtotal?.subItems?.map((subItem) => {
                      if (subItem?.type === PriceBreakdownLineItemType.SUBTOTAL_TOTAL_PRICE) {
                        return null
                      }
                      return (
                        <PriceLine
                          $isSubtotalBreakdownStart={
                            subItem?.type === PriceBreakdownLineItemType.PRODUCT_PRICE
                          }
                          key={subItem?.type}
                        >
                          <DetailTitle data-qa={getDataQaLabel(subItem?.type)}>
                            {subItem?.label}
                          </DetailTitle>
                          <DetailValue data-qa={getDataQaAmount(subItem?.type)}>
                            {subItem?.formattedAmount}
                          </DetailValue>
                        </PriceLine>
                      )
                    })}
                    <Divider />
                  </div>
                )}
            </div>
          )
        })}
      </Container>
      <Divider />
      <PriceLine>
        <FinalPriceTitle data-qa={CheckoutQA.OrderSummaryOrderTotalTag}>
          {order?.pricingBreakdown?.total?.label}
        </FinalPriceTitle>
        <FinalPriceValue data-qa={`OrderSummaryTotalAmount${CheckoutQA.FinalCheckout}`}>
          {order?.pricingBreakdown?.total?.formattedAmount}
        </FinalPriceValue>
      </PriceLine>
      {isWantOrder && order?.pricingBreakdown?.total?.localizedAmount?.currency !== 'USD' && (
        <WantOrderUsdConversion>
          <FinalPriceValue data-qa={`OrderSummaryTotalAmount${CheckoutQA.FinalCheckout}USD`}>
            {formatMoneyObject({
              currency: 'USD',
              amount: order?.pricingBreakdown?.total?.localizedAmount?.amountUsdCents,
              amountUsdCents: order?.pricingBreakdown?.total?.localizedAmount?.amountUsdCents,
            })}
          </FinalPriceValue>
        </WantOrderUsdConversion>
      )}
      <ApplicableCostsModal
        body={order?.applicableCostsNote}
        isOpen={isApplicableCostsModalOpen}
        onClose={handleCloseModal}
      />
    </>
  )
}

const getDataQaAmount = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.APPLICABLE_COSTS:
      return CheckoutQA.OrderSummaryApplicableCostsAmount
    case PriceBreakdownLineItemType.CREDIT:
      return CheckoutQA.OrderSummaryCreditAmount
    case PriceBreakdownLineItemType.PRIORITY_PROCESSING:
      return CheckoutQA.OrderSummaryPriorityProcessingFeeAmount
    case PriceBreakdownLineItemType.PRODUCT_PRICE:
      return CheckoutQA.OrderSummaryListPriceAmount
    case PriceBreakdownLineItemType.PROMOTION:
      return CheckoutQA.OrderSummarySubtotalAmount
    case PriceBreakdownLineItemType.SHIPPING:
      return CheckoutQA.OrderSummaryShippingAmount
    case PriceBreakdownLineItemType.SUBTOTAL:
      return CheckoutQA.OrderSummarySubtotalAmount
    case PriceBreakdownLineItemType.INCLUDED_TAX_AND_DUTY:
    case PriceBreakdownLineItemType.TAX:
      return CheckoutQA.OrderSummaryTaxAmount
    default:
      return ''
  }
}

const getDataQaLabel = (type: string) => {
  switch (type) {
    case PriceBreakdownLineItemType.APPLICABLE_COSTS:
      return CheckoutQA.OrderSummaryApplicableCostsTag
    case PriceBreakdownLineItemType.CREDIT:
      return CheckoutQA.OrderSummaryCreditTag
    case PriceBreakdownLineItemType.PRIORITY_PROCESSING:
      return CheckoutQA.OrderSummaryPriorityProcessingFeeTag
    case PriceBreakdownLineItemType.PRODUCT_PRICE:
      return CheckoutQA.OrderSummaryListPriceTag
    case PriceBreakdownLineItemType.PROMOTION:
      return CheckoutQA.OrderSummarySubtotalTag
    case PriceBreakdownLineItemType.SHIPPING:
      return CheckoutQA.OrderSummaryShippingTag
    case PriceBreakdownLineItemType.SUBTOTAL:
      return CheckoutQA.OrderSummarySubtotalTag
    case PriceBreakdownLineItemType.INCLUDED_TAX_AND_DUTY:
    case PriceBreakdownLineItemType.TAX:
      return CheckoutQA.OrderSummaryTaxTag
    default:
      return ''
  }
}

const Caret = styled(RotatingCaret)`
  height: 4px;
  margin-bottom: 2px;
  margin-left: 4px;
  margin-top: 2px;
  width: 8px;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
`
const DetailTitle = styled.div`
  color: ${colors.FC2_GREY};
  margin-right: 5px;
`
const DetailTitleButton = styled.button`
  color: ${colors.FC2_GREY};
  margin-right: 5px;
  text-decoration: underline;
`
const DetailValue = styled.div<{ $hasNumber: boolean; $type: string }>`
  ${({ $hasNumber }) => !$hasNumber && 'font-weight: normal;'};
  ${fonts.SUBTITLE_3}
  color: ${({ $type }) =>
    $type === PriceBreakdownLineItemType.CREDIT || $type === PriceBreakdownLineItemType.PROMOTION
      ? `${colors.FC2_RED}`
      : `${colors.FC2_BLACK}`};
  margin: 0;
`
const FinalPriceTitle = styled.div`
  ${fonts.SUBTITLE_3}
`
const FinalPriceValue = styled.div`
  ${fonts.SUBTITLE_1}
`
const PriceLine = styled.div<{ $isSubtotalBreakdownStart?: boolean }>`
  align-items: flex-end;
  color: ${colors.FC2_DARK_GREY};
  display: flex;
  justify-content: space-between;
  margin-top: ${({ $isSubtotalBreakdownStart }) => ($isSubtotalBreakdownStart ? '20px' : '10px')};
`

const WantOrderUsdConversion = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`
